import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  FORMAT_LABEL,
  NewArticleOpportunity,
  REGION_INFO,
  SeoItem,
} from '../types';

const SOURCE_LABEL = {
  gsc: 'Dosaze already shows up for it',
  paid: 'A search we pay for in Google Ads',
  idea: 'A related search from Keyword Planner',
};

// The article is rendered in a sandboxed iframe: no scripts run, links open in
// a new tab against the store's own site.
export function previewDoc(html: string, title: string, site = 'dosaze.com') {
  return `<!doctype html><html><head><meta charset="utf-8"><base href="https://${site}/" target="_blank">
<style>
  body { font: 16px/1.6 Georgia, 'Times New Roman', serif; color: #1f2328; margin: 24px; max-width: 720px; }
  h1 { font-size: 30px; line-height: 1.25; margin: 0 0 16px; }
  h2 { font-size: 22px; margin: 28px 0 8px; } h3 { font-size: 18px; margin: 20px 0 6px; }
  a { color: #1d4ed8; } table { border-collapse: collapse; width: 100%; font-size: 14px; margin: 12px 0; }
  th, td { border: 1px solid #d0d7de; padding: 6px 8px; text-align: left; vertical-align: top; } th { background: #f6f8fa; }
</style></head><body><h1>${title.replace(/</g, '&lt;')}</h1>${html}</body></html>`;
}

type Decision = 'approve' | 'reject' | 'changes';

interface Props {
  item: SeoItem;
  actor: string;
  // The topic header and "why this topic"; off inside a decision record, which
  // shows the full observations instead.
  showTopic?: boolean;
  onApprove: (item: SeoItem) => Promise<void>;
  onReject: (item: SeoItem, reason: string) => Promise<void>;
  onRequestChanges: (item: SeoItem, feedback: string) => Promise<void>;
}

export default function ItemDetail({
  item,
  actor,
  showTopic = true,
  onApprove,
  onReject,
  onRequestChanges,
}: Props) {
  // Only rendered for new articles (DecisionDetail shows title rewrites itself).
  const { draft, validation: v } = item;
  const o = item.opportunity as NewArticleOpportunity;
  const store = REGION_INFO[item.region ?? 'US'];
  const [open, setOpen] = React.useState<Decision | null>(null);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setOpen(null);
    setText('');
    setError(null);
  }, [item.id]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (open === 'approve') await onApprove(item);
      if (open === 'reject') await onReject(item, text);
      if (open === 'changes') await onRequestChanges(item, text);
      setOpen(null);
      setText('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const reviewable = item.status === 'proposed';
  const position = o.evidence.dosazeBestPosition;

  return (
    <Stack spacing={2}>
      {showTopic && (
        <>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Target search
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              “{o.keyword}”
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
            >
              <Chip size="small" label={FORMAT_LABEL[o.format]} />
              <Chip
                size="small"
                variant="outlined"
                label={`${o.monthlySearches.toLocaleString()} US searches / month`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={
                  position
                    ? `Dosaze ranks ~${position} today`
                    : 'Dosaze doesn’t rank for it yet'
                }
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Links to ${o.collection.path}`}
              />
            </Stack>
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Why this topic
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {SOURCE_LABEL[o.evidence.keywordSource]}. No Dosaze page is in the
              top 10 for it, our own pages aren’t already competing for it, and
              no existing blog article covers it. Also covers:{' '}
              {o.variants.filter(k => k !== o.keyword).join(', ') || '—'}.
            </Typography>
          </Paper>
        </>
      )}

      {item.lastError && <Alert severity="error">{item.lastError}</Alert>}
      {v.warnings.map(w => (
        <Alert key={w} severity="warning">
          {w}
        </Alert>
      ))}
      {v.info.map(i => (
        <Alert key={i} severity="info">
          {i}
        </Alert>
      ))}
      {item.status === 'changes_requested' && (
        <Alert severity="info">
          Sent back: “{item.feedback}”. It will be redrafted on the next daily
          run.
        </Alert>
      )}
      {item.status === 'rejected' && (
        <Alert severity="info">
          Rejected{item.decidedBy ? ` by ${item.decidedBy}` : ''}
          {item.reason ? `: “${item.reason}”` : ''}.
        </Alert>
      )}
      {item.published && (
        <Alert
          severity="success"
          action={
            <Button
              size="small"
              href={item.published.url}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon />}
            >
              Open
            </Button>
          }
        >
          Published{item.decidedBy ? ` by ${item.decidedBy}` : ''} on{' '}
          {new Date(item.published.publishedAt).toLocaleDateString()}.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          How it may look in Google
        </Typography>
        <Typography sx={{ color: '#1a0dab', fontSize: 18 }}>
          {draft.title}
        </Typography>
        <Typography sx={{ color: '#006621', fontSize: 13 }}>
          {store.site} › blogs › {store.blog} › {draft.handle}
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#4d5156' }}>
          {draft.metaDescription}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1 }}
        >
          {v.words.toLocaleString()} words · {v.links} links to Dosaze pages ·
          tags: {draft.tags.join(', ')}
          {item.revisions ? ` · redrafted ${item.revisions}×` : ''}
        </Typography>
      </Paper>

      {reviewable && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="success"
            onClick={() => setOpen('approve')}
          >
            Approve &amp; publish
          </Button>
          <Button variant="outlined" onClick={() => setOpen('changes')}>
            Request changes
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setOpen('reject')}
          >
            Reject
          </Button>
        </Stack>
      )}

      <Divider />
      <Box
        component="iframe"
        title={`Preview: ${draft.title}`}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={previewDoc(draft.html, draft.title, store.site)}
        sx={{
          width: '100%',
          height: '70vh',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      />

      <Dialog
        open={open !== null}
        onClose={() => !busy && setOpen(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {open === 'approve' && 'Publish this article?'}
          {open === 'changes' && 'What should change?'}
          {open === 'reject' && 'Reject this draft?'}
        </DialogTitle>
        <DialogContent>
          {open === 'approve' && (
            <DialogContentText>
              “{draft.title}” goes live on the Dosaze blog in {store.country}{' '}
              right away, by “Dosaze Sleep Team”. You can unpublish it later in
              Shopify admin.
            </DialogContentText>
          )}
          {open === 'reject' && (
            <DialogContentText sx={{ mb: 1 }}>
              The topic won’t be proposed again. A reason helps the system learn
              what to avoid.
            </DialogContentText>
          )}
          {open === 'changes' && (
            <DialogContentText sx={{ mb: 1 }}>
              Your notes go to the writer; the redraft appears here after
              tomorrow morning’s run.
            </DialogContentText>
          )}
          {open !== 'approve' && (
            <TextField
              id="seo-decision-text"
              autoFocus
              fullWidth
              multiline
              minRows={3}
              label={open === 'changes' ? 'Feedback' : 'Reason (optional)'}
              value={text}
              onChange={e => setText(e.target.value)}
            />
          )}
          {!actor && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Set your name (top right) so decisions are attributed to you.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)} disabled={busy}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color={
              open === 'reject'
                ? 'error'
                : open === 'approve'
                  ? 'success'
                  : 'primary'
            }
            loading={busy}
            disabled={open === 'changes' && text.trim().length < 3}
            onClick={submit}
          >
            {open === 'approve' && 'Publish'}
            {open === 'changes' && 'Send back'}
            {open === 'reject' && 'Reject'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
